import React, { useState, useRef, useEffect } from "react";
import LogoCarousel from "../components/IntegrationSupport/IntegrationSupport";
import Footer from "../../../components/Footer/Footer";
import VoiceAIForm from "../CustomUsecaseForm/CustomUsecaseForm";
import Navbar from "../../../components/NavigationMenu/NavBar";
import { Button, Modal } from "@mui/material";
import { Mic, Close } from "@mui/icons-material";
import useSEO from "../../../hooks/useSEO";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Grid,
  Popover,
  Paper,
  CircularProgress,
} from "@mui/material";
import { CheckCircle, ArrowForward } from "@mui/icons-material";
import Vapi from "@vapi-ai/web";
import { voiceAgentService } from "../../../services/VoiceAgentService";
import CalendarModal from "../../../components/CalendarModal/CalendarModal";

const vapi = new Vapi(process.env.REACT_APP_VAPI_PUBLIC_KEY);

// ====== IMPORTANT: set your current Web App URL here (Deploy -> Web app -> Anyone) ======
const GAS_URL =
  "https://script.google.com/macros/s/AKfycbx2fKm1EpiWyfaX__ja7al7ICSjhtdZTK1b7FJM10t77alEDT5DqnWnNK4MLS_kvt4t/exec";

// ---------- tiny JSONP utility (no CORS needed) ----------
function jsonp(url, params = {}, timeoutMs = 12000) {
  return new Promise((resolve, reject) => {
    const cbName = "cb_" + Math.random().toString(36).slice(2);
    const script = document.createElement("script");
    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error("JSONP timeout"));
    }, timeoutMs);

    function cleanup() {
      clearTimeout(timeout);
      delete window[cbName];
      if (script.parentNode) script.parentNode.removeChild(script);
    }

    window[cbName] = (data) => {
      cleanup();
      resolve(data);
    };

    const qs = new URLSearchParams({ ...params, callback: cbName }).toString();
    script.src = `${url}?${qs}`;
    script.onerror = () => {
      cleanup();
      reject(new Error("JSONP network error"));
    };
    document.head.appendChild(script);
  });
}

// ---------- Google Sheets service (JSONP) ----------
const GoogleSheetsService = {
  async saveUserData(userData) {
    const params = {
      action: "saveUserData",
      email: userData.email,
      phoneNumber: "", // Remove phone number data
      assistantName: userData.assistantName || "Unknown Assistant",
      pageUrl: typeof window !== "undefined" ? window.location.href : "",
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      referrer: typeof document !== "undefined" ? document.referrer || "" : "",
      timestamp: new Date().toISOString(),
    };
    // JSONP returns a real object (not an opaque response), so you can branch on success
    const result = await jsonp(GAS_URL, params);
    if (result?.success === false) {
      throw new Error(result?.error || result?.message || "Submission failed");
    }
    return result;
  },

  async testConnection() {
    return jsonp(GAS_URL, { action: "ping" });
  },
};

// ------------------- User Details Popup -------------------
const UserDetailsPopup = ({ open, anchorEl, onClose, onSubmit, loading }) => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    setEmailError("");

    if (!email.trim()) {
      setEmailError("Email is required");
      return;
    }
    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email");
      return;
    }

    onSubmit({
      email: email.trim(),
    });
  };

  const handleClose = () => {
    setEmail("");
    setEmailError("");
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
      slotProps={{
        paper: {
          sx: {
            mt: 1,
            borderRadius: "16px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            backdropFilter: "blur(10px)",
            maxWidth: "340px",
            width: "100%",
          },
        },
      }}
    >
      <Paper
        sx={{
          p: 3,
          background:
            "linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)",
          borderRadius: "16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: "#333", fontSize: "1.1rem" }}
          >
            Quick Details
          </Typography>
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{
              minWidth: "auto",
              p: 0.5,
              color: "#666",
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
            }}
          >
            <Close sx={{ fontSize: 20 }} />
          </Button>
        </Box>

        <Typography
          variant="body2"
          sx={{ color: "#666", mb: 2.5, fontSize: "0.875rem", lineHeight: 1.4 }}
        >
          Enter your email to start chatting with our AI agent
        </Typography>

        <form onSubmit={handleSubmit}>
          <Box sx={{ mb: 2.5 }}>
            <TextField
              fullWidth
              type="email"
              label="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!emailError}
              helperText={emailError}
              size="small"
              disabled={loading}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  "& fieldset": { borderColor: "rgba(0, 0, 0, 0.23)" },
                  "&:hover fieldset": { borderColor: "#ea6347" },
                  "&.Mui-focused fieldset": {
                    borderColor: "#ea6347",
                    borderWidth: "2px",
                  },
                },
                "& .MuiInputBase-input": { fontSize: "0.875rem" },
                "& .MuiInputLabel-root": { fontSize: "0.875rem" },
              }}
            />
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: "#ea6347",
              "&:hover": { backgroundColor: "#d55640" },
              "&:disabled": { backgroundColor: "rgba(234, 99, 71, 0.6)" },
              py: 1.2,
              fontSize: "0.9rem",
              fontWeight: 600,
              borderRadius: "8px",
              textTransform: "none",
              boxShadow: "0 4px 12px rgba(234, 99, 71, 0.3)",
              "&:hover": { boxShadow: "0 6px 16px rgba(234, 99, 71, 0.4)" },
              position: "relative",
            }}
          >
            {loading && (
              <CircularProgress
                size={20}
                sx={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  marginLeft: "-10px",
                  marginTop: "-10px",
                  color: "white",
                }}
              />
            )}
            <span style={{ opacity: loading ? 0 : 1 }}>
              {loading ? "Starting..." : "Start Conversation"}
            </span>
          </Button>
        </form>
      </Paper>
    </Popover>
  );
};

// ------------------- Page -------------------
const testimonials = [
  {
    name: "Sarah Johnson",
    role: "CTO, TechCorp",
    content:
      "Beanbag voice agents reduced our support costs by 60% while improving customer satisfaction.",
    rating: 5,
  },
  {
    name: "Michael Chen",
    role: "Head of Operations, StartupXYZ",
    content:
      "The implementation was seamless and the results were immediate. Highly recommended!",
    rating: 5,
  },
  {
    name: "Emily Rodriguez",
    role: "Customer Success Manager, Enterprise Inc",
    content:
      "Our customers love the natural conversations. It feels like talking to a real person.",
    rating: 5,
  },
];

const benefits = [
  {
    number: "3x",
    title: "Longer Responses",
    description:
      "Customers provide more detailed feedback compared to traditional methods.",
  },
  {
    number: "<24h",
    title: "Fast Results",
    description: "Get actionable insights within hours.",
  },
  {
    number: "+30M",
    title: "Possible Respondents",
    description: "Reach a broad audience with ease.",
  },
];

const ReusableDemoPage = ({ config }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const [callInitiated, setCallInitiated] = useState(false);
  const [isCallActive, setIsCallActive] = useState(false);
  const [callStatus, setCallStatus] = useState("");

  const [userDetailsPopupOpen, setUserDetailsPopupOpen] = useState(false);
  const [micButtonRef, setMicButtonRef] = useState(null);
  const [isSubmittingUserDetails, setIsSubmittingUserDetails] = useState(false);
  const [userDetailsSubmitted, setUserDetailsSubmitted] = useState(false);
  const [submittedUserData, setSubmittedUserData] = useState(null);

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleVoiceToggle = async (event) => {
    if (!userDetailsSubmitted && !isVoiceActive) {
      setMicButtonRef(event.currentTarget);
      setUserDetailsPopupOpen(true);
      return;
    }
    if (!isVoiceActive) {
      try {
        vapi.start(config.assistantId);
        setIsVoiceActive(true);
      } catch (error) {
        console.error("Error starting Vapi assistant:", error);
        alert(
          "Failed to start voice interaction. Please check microphone permissions."
        );
      }
    } else {
      try {
        vapi.stop();
        setIsVoiceActive(false);
      } catch (error) {
        console.error("Error stopping Vapi assistant:", error);
      }
    }
  };

  const handleUserDetailsSubmit = async (userDetails) => {
    setIsSubmittingUserDetails(true);
    try {
      // Save to Google Sheets via JSONP (no CORS)
      const result = await GoogleSheetsService.saveUserData({
        ...userDetails,
        assistantName: config.assistantName || "Unknown Assistant",
      });

      // If Apps Script returned success, proceed
      setSubmittedUserData(userDetails);
      setUserDetailsSubmitted(true);
      setUserDetailsPopupOpen(false);

      try {
        vapi.start(config.assistantId);
        setIsVoiceActive(true);
      } catch (error) {
        console.error("Error starting Vapi assistant:", error);
        alert(
          "Details saved, but couldn't start voice. Check microphone permissions and try again."
        );
      }
    } catch (error) {
      console.error("Error submitting user details:", error);
      alert(`Failed to submit details: ${error.message}. Please try again.`);
    } finally {
      setIsSubmittingUserDetails(false);
    }
  };

  const handleCloseUserDetailsPopup = () => {
    setUserDetailsPopupOpen(false);
    setMicButtonRef(null);
  };

  const features = [
    {
      icon: "⚡",
      title: "Efficiency",
      description: "Get results in hours, not weeks.",
    },
    {
      icon: "👤",
      title: "Personalization",
      description:
        "Engage with customers through personalized voice interactions.",
    },
    {
      icon: "📈",
      title: "Scalability",
      description: "Reach millions of users with ease.",
    },
    {
      icon: "🔒",
      title: "Security",
      description:
        "Your data is safe with us. We comply with the highest standards.",
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    vapi.on("call-start", () => {
      setIsCallActive(true);
      setCallStatus("Voice interaction started");
    });

    vapi.on("call-end", () => {
      setIsCallActive(false);
      setCallStatus("Voice interaction ended");
      setIsVoiceActive(false);
    });

    vapi.on("error", (error) => {
      console.error("VAPI Error:", error);
      setCallStatus("Voice interaction failed");
      setIsCallActive(false);
      setIsVoiceActive(false);
    });

    return () => {
      vapi.removeAllListeners();
    };
  }, []);

  const styles = {
    body: {
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      lineHeight: "1.6",
      color: "#333",
      background: "#ffffff",
      margin: 0,
      padding: 0,
    },
    container: {
      maxWidth: "1200px",
      margin: "0 auto",
      padding: "0 0px",
    },
    header: {
      position: "fixed",
      top: 0,
      width: "100%",
      background: isScrolled
        ? "rgba(255, 255, 255, 0.98)"
        : "rgba(255, 255, 255, 0.95)",
      backdropFilter: "blur(10px)",
      zIndex: 1000,
      transition: "all 0.3s ease",
      boxShadow: isScrolled ? "0 2px 20px rgba(0, 0, 0, 0.1)" : "none",
    },
    nav: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    hero: {
      padding: "120px 0 80px",
      background:
        "radial-gradient(ellipse at top, rgba(226, 107, 74, 0.05) 0%, transparent 70%)",
    },
    heroTitle: {
      fontSize: "clamp(2.5rem, 5vw, 4rem)",
      fontWeight: "700",
      marginBottom: "24px",
      background: "linear-gradient(135deg, #333,rgb(226, 104, 74))",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
      animation: "fadeInUp 1s ease-out",
    },
    heroText: {
      fontSize: "1.25rem",
      maxWidth: "800px",
      margin: "0 auto 40px",
      color: "#666",
      animation: "fadeInUp 1s ease-out 0.2s both",
    },
    voiceAgentSection: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minWidth: "250px",
    },
    voiceAgentContainer: {
      marginBottom: "48px",
      marginTop: "32px",
    },
    voiceAgentCircle: {
      position: "relative",
      margin: "0 auto",
      width: "120px", // Reduced from 192px
      height: "120px", // Reduced from 192px
      borderRadius: "50%",
      background: "linear-gradient(135deg, #f09481, #ea6347)",
      padding: "3px", // Reduced from 4px
      cursor: "pointer",
      transition: "all 0.3s ease",
      boxShadow: "0 4px 20px rgba(226, 107, 74, 0.3)",
    },
    voiceAgentCircleActive: {
      animation: "pulse 2s infinite",
      boxShadow: "0 8px 40px rgba(226, 107, 74, 0.5)",
      transform: "scale(1.05)",
    },
    voiceAgentInner: {
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      background: "white",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: "1px solid rgba(0, 0, 0, 0.1)",
    },
    voiceAgentIconContainer: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    voiceAgentIcon: {
      fontSize: "50px", // Reduced from 80px
      color: "#ea6347",
      zIndex: 2,
    },
    voiceAgentPulse: {
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: "50px", // Reduced from 80px
      height: "50px", // Reduced from 80px
      border: "3px solid #ea6347", // Reduced from 4px
      borderRadius: "50%",
      animation: "ping 2s infinite",
    },
    voiceAgentText: {
      color: "#666",
      marginTop: "16px", // Reduced from 24px
      fontSize: "1rem", // Reduced from 1.125rem
      fontWeight: "500",
    },
    clientLogos: {
      padding: "60px 0",
      background: "#f8f9fa",
      overflow: "hidden",
    },
    section: {
      padding: "80px 0",
    },
    sectionAlt: {
      padding: "40px 0",
      background: "#f8f9fa",
    },
    sectionTitle: {
      fontSize: "2.5rem",
      fontWeight: "700",
      textAlign: "center",
      marginBottom: "40px",
      color: "#333",
      margin: "10px",
    },
    voiceSection: {
      padding: "80px 0",
      textAlign: "center",
    },
    voiceContent: {
      maxWidth: "600px",
      margin: "0 auto",
      padding: "0 10px",
    },
    benefitsGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
      gap: "20px",
      marginTop: "20px",
      marginLeft: "40px",
      marginRight: "40px",
    },
    benefit: {
      textAlign: "center",
      padding: "40px 30px",
      background: "white",
      borderRadius: "15px",
      boxShadow: "0 5px 15px rgba(0, 0, 0, 0.08)",
      height: "13rem",
    },
    benefitNumber: {
      fontSize: "3rem",
      fontWeight: "700",
      color: "#ea6347",
      display: "block",
      marginBottom: "10px",
    },
    testimonial: {
      padding: "80px 0",
      textAlign: "center",
    },
    testimonialContent: {
      maxWidth: "800px",
      margin: "0 auto",
      padding: "60px 40px",
      background: "white",
      borderRadius: "20px",
      boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
      position: "relative",
    },
    testimonialText: {
      fontSize: "1rem",
      lineHeight: "1.6",
      marginBottom: "1.5rem",
      fontStyle: "italic",
      color: "#333",
    },
    testimonialAuthor: {
      fontWeight: "600",
      color: "#ea6347",
    },
    security: {
      padding: "80px 0",
      background: "#f8f9fa",
      textAlign: "center",
    },
    securityBadges: {
      display: "flex",
      justifyContent: "center",
      gap: "40px",
      marginTop: "40px",
      flexWrap: "wrap",
    },
    badge: {
      padding: "15px 30px",
      background: "white",
      borderRadius: "50px",
      fontWeight: "600",
      color: "#ea6347",
      boxShadow: "0 5px 15px rgba(0, 0, 0, 0.1)",
    },
    featureTitle: {
      fontSize: "1.25rem",
      fontWeight: "600",
      marginBottom: "10px",
      color: "#333",
    },
    featureText: {
      color: "#666",
    },
    testimonialCard: {
      backgroundColor: "#ffffff",
      padding: "2rem",
      borderRadius: "12px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      minWidth: "300px",
      flex: "1",
      border: "1px solid #e0e0e0",
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
      margin: "10px",
    },
    testimonialRating: {
      fontSize: "1.2rem",
      color: "#ffc107",
      marginBottom: "1rem",
    },
    testimonialsRow: {
      display: "flex",
      gap: "2rem",
      marginTop: "2rem",
      overflowX: "auto",
      padding: "1rem 0",
    },
    featureItem: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "16px",
      background: "#ffffff",
      borderRadius: "8px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
      transition: "all 0.3s ease",
    },
    featuresGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, 1fr)",
      gridTemplateRows: "repeat(2, 1fr)",
      gap: "24px",
      marginTop: "32px",
    },
    rightSection: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "24px",
      minWidth: "320px",
      padding: "32px",
      background: "rgba(255, 255, 255, 0.95)",
      borderRadius: "20px",
      backdropFilter: "blur(10px)",
      border: "1px solid rgba(255, 255, 255, 0.3)",
      boxShadow: "0 3px 12px rgba(0, 0, 0, 0.06)",
    },
  };

  useSEO({
    title: config.seo.title,
    description: config.seo.description,
    canonical: config.seo.canonical,
    keywords: config.seo.keywords,
    ogTitle: config.seo.ogTitle,
    ogDescription: config.seo.ogDescription,
    ogImage: config.seo.ogImage,
    ogUrl: config.seo.ogUrl,
    ogType: "website",
    twitterCard: "summary_large_image",
    twitterSite: "@BeanbagAI",
    jsonLd: config.seo.jsonLd,
  });

  return (
    <div style={styles.body}>
      <style>
        {`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
              box-shadow: 0 4px 20px rgba(226, 107, 74, 0.3);
            }
            50% {
              transform: scale(1.05);
              box-shadow: 0 8px 40px rgba(226, 107, 74, 0.5);
            }
          }

          @keyframes ping {
            0% {
              transform: translate(-50%, -50%) scale(1);
              opacity: 1;
            }
            75%, 100% {
              transform: translate(-50%, -50%) scale(2);
              opacity: 0;
            }
          }

          .feature-item:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 16px rgba(234, 99, 71, 0.15);
          }

          @media (max-width: 768px) {
            .section-title {
              font-size: 2rem !important;
            }
            .hero-title {
              font-size: 2.5rem !important;
            }
            .hero-text {
              font-size: 1.1rem !important;
            }
            .hero-container {
              flex-direction: column !important;
              align-items: center;
              gap: 3rem;
            }
            .hero-content {
              max-width: 100% !important;
              text-align: center;
            }
            .right-section {
              min-width: unset !important;
              width: 100%;
              max-width: 400px;
            }
            .features-grid {
              grid-template-columns: 1fr !important;
              grid-template-rows: repeat(6, 1fr) !important;
            }
          }

          @media (max-width: 1024px) {
            .features-grid {
              grid-template-columns: repeat(2, 1fr) !important;
              grid-template-rows: repeat(3, 1fr) !important;
            }
          }
        `}
      </style>

      <header style={styles.header}>
        <div style={styles.container}>
          <nav style={styles.nav}>
            <Navbar />
          </nav>
        </div>
      </header>

      <main>
        <section style={styles.hero}>
          <div
            style={{
              ...styles.container,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "4rem",
            }}
            className="hero-container"
          >
            <div
              style={{ flex: 1, maxWidth: "600px" }}
              className="hero-content"
            >
              <h1 style={styles.heroTitle} className="hero-title">
                {config.heroTitle || `Unlock deeper customer insights for ${config.assistantName}`}
              </h1>
              <p style={styles.heroText} className="hero-text">
                {config.heroDescription}
              </p>
              <Button
                variant="text"
                onClick={handleOpenModal}
                sx={{
                  color: "black",
                  textDecoration: "underline",
                  textTransform: "none",
                  fontSize: { xs: "16px", sm: "18px", md: "20px" },
                  fontWeight: 500,
                  p: "8px 16px",
                  borderRadius: "8px",
                  position: "relative",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "rgba(234, 99, 71, 0.1)",
                    color: "#ea6347",
                    textDecoration: "none",
                    transform: "translateY(-2px)",
                  },
                  "&:active": {
                    transform: "translateY(0px)",
                  },
                }}
              >
                Book a Demo
              </Button>
            </div>

            <div style={styles.rightSection} className="right-section">
              <div
                style={{
                  ...styles.voiceAgentCircle,
                  ...(isVoiceActive ? styles.voiceAgentCircleActive : {}),
                }}
                onClick={handleVoiceToggle}
              >
                <div style={styles.voiceAgentInner}>
                  <div style={{ position: "relative" }}>
                    <Mic style={styles.voiceAgentIcon} />
                    {isVoiceActive && (
                      <div style={styles.voiceAgentPulse}></div>
                    )}
                  </div>
                </div>
              </div>

              <Typography
                sx={{
                  color: "#666",
                  fontSize: "0.875rem",
                  textAlign: "center",
                  fontWeight: "500",
                }}
              >
                {isVoiceActive
                  ? "Click to Stop"
                  : userDetailsSubmitted
                  ? `Try ${config.assistantName} Agent`
                  : config.micText || `Talk to ${config.assistantName} Agent`}
              </Typography>

              {userDetailsSubmitted && submittedUserData && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: "rgba(234, 99, 71, 0.1)",
                    borderRadius: "8px",
                    border: "1px solid rgba(234, 99, 71, 0.2)",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#333",
                      fontSize: "0.8rem",
                      textAlign: "center",
                    }}
                  >
                    Connected as: {submittedUserData.email}
                  </Typography>
                </Box>
              )}
            </div>
          </div>
        </section>

        <section style={styles.clientLogos}>
          <LogoCarousel />
        </section>

        <section style={styles.section}>
          <div style={styles.container}>
            <div style={{ textAlign: "center", marginBottom: "64px" }}>
              <Typography
                variant="h4"
                component="h3"
                sx={{
                  marginBottom: "16px",
                  color: "#333",
                  fontWeight: "700",
                }}
              >
                {config.sectionTitle}
              </Typography>
              <p
                style={{
                  fontSize: "1.25rem",
                  color: "#666",
                  maxWidth: "800px",
                  margin: "0 auto",
                }}
              >
                {config.sectionDescription}
              </p>
            </div>

            <Card
              sx={{
                backgroundColor: "white",
                boxShadow: "0 3px 12px rgba(0, 0, 0, 0.06)",
                borderRadius: "16px",
                maxWidth: "1000px",
                margin: "0 auto",
                padding: "32px",
              }}
            >
              <Typography
                variant="h5"
                component="h4"
                sx={{
                  marginBottom: "24px",
                  color: "#333",
                  fontWeight: "600",
                  textAlign: "center",
                }}
              >
                Key Features
              </Typography>

              <div style={styles.featuresGrid} className="features-grid">
                {config.features.map((feature, index) => (
                  <div
                    key={index}
                    style={styles.featureItem}
                    className="feature-item"
                  >
                    <CheckCircle
                      sx={{
                        color: "#ea6347",
                        fontSize: "20px",
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        fontWeight: "500",
                        fontSize: "0.9rem",
                      }}
                    >
                      {feature}
                    </Typography>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </section>

        <section style={styles.sectionAlt}>
          <div style={styles.container}>
            <div
              style={{
                textAlign: "center",
                marginBottom: "64px",
                padding: "10px",
              }}
            >
              <h2 style={styles.sectionTitle} className="section-title">
                Why Choose Beanbag AI?
              </h2>
              <p
                style={{
                  fontSize: "1.25rem",
                  color: "#666",
                  maxWidth: "800px",
                  margin: "0 auto",
                }}
              >
                Built for enterprise scale with cutting-edge AI technology
              </p>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: "24px",
                marginTop: "40px",
              }}
            >
              {features.map((feature, index) => (
                <Card
                  key={index}
                  sx={{
                    margin: "10px",
                    backgroundColor: "white",
                    border: "1px solid rgba(0, 0, 0, 0.1)",
                    borderRadius: "12px",
                    boxShadow: "0 3px 12px rgba(0, 0, 0, 0.06)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderColor: "rgba(234, 99, 71, 0.5)",
                      boxShadow: "0 8px 25px rgba(234, 99, 71, 0.15)",
                      transform: "translateY(-4px)",
                    },
                  }}
                >
                  <CardContent sx={{ padding: "24px" }}>
                    <div
                      style={{
                        width: "48px",
                        height: "48px",
                        background: "linear-gradient(135deg, #f09481, #ea6347)",
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "16px",
                      }}
                    >
                      <div style={{ color: "white", fontSize: "24px" }}>
                        {feature.icon}
                      </div>
                    </div>
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{
                        fontSize: "1.25rem",
                        fontWeight: "600",
                        marginBottom: "8px",
                        color: "#333",
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#666",
                        lineHeight: "1.6",
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section style={styles.voiceSection}>
          <div style={styles.container}>
            <div style={styles.voiceContent}>
              <h2 style={styles.sectionTitle} className="section-title">
                More Than Just Words
              </h2>
              <p style={{ ...styles.heroText, animation: "none" }}>
                Voice interactions capture the nuances of human
                communication—tone, inflection, and emotion. Beanbag AI goes
                beyond text to uncover deeper user sentiment, providing you with
                richer insights.
              </p>
            </div>
          </div>
        </section>

        <section style={styles.sectionAlt}>
          <div style={styles.container}>
            <h2 style={styles.sectionTitle} className="section-title">
              Key Benefits
            </h2>
            <div style={styles.benefitsGrid} className="benefits-grid">
              {benefits.map((benefit, index) => (
                <div key={index} style={styles.benefit}>
                  <span style={styles.benefitNumber}>{benefit.number}</span>
                  <h3 style={styles.featureTitle}>{benefit.title}</h3>
                  <p style={styles.featureText}>{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section style={styles.testimonial}>
          <div style={styles.container}>
            <h2 style={styles.sectionTitle} className="section-title">
              What Our Customers Say
            </h2>
            <div style={styles.testimonialsRow}>
              {testimonials.map((testimonial, index) => (
                <div key={index} style={styles.testimonialCard}>
                  <div style={styles.testimonialRating}>
                    {"★".repeat(testimonial.rating)}
                  </div>
                  <p style={styles.testimonialText}>"{testimonial.content}"</p>
                  <p style={styles.testimonialAuthor}>
                    — {testimonial.name}, {testimonial.role}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section style={styles.security}>
          <div style={styles.container}>
            <h2 style={styles.sectionTitle} className="section-title">
              Your Data is Safe with Us
            </h2>
            <p style={styles.featureText}>
              We prioritize your data security. Beanbag AI is compliant with
              industry standards.
            </p>
            <div style={styles.securityBadges}>
              <div style={styles.badge}>SOC 2 Type II</div>
              <div style={styles.badge}>GDPR Compliant</div>
            </div>
          </div>
        </section>
      </main>

      <div id="contact" style={{ padding: "20px" }}>
        <VoiceAIForm />
        <Footer />
      </div>

      <CalendarModal open={modalOpen} handleClose={handleCloseModal} />

      <UserDetailsPopup
        open={userDetailsPopupOpen}
        anchorEl={micButtonRef}
        onClose={handleCloseUserDetailsPopup}
        onSubmit={handleUserDetailsSubmit}
        loading={isSubmittingUserDetails}
      />
    </div>
  );
};

export default ReusableDemoPage;
